import { BadgeCheck, HeartHandshake, Truck, TrendingUp, ClipboardCheck, PackageCheck, Wheat, Network } from "lucide-react";
import { Reveal, Eyebrow } from "@/components/motion";

const features = [
  { icon: BadgeCheck, title: "Quality First", text: "A consistent focus on product quality and responsible presentation." },
  { icon: HeartHandshake, title: "Farmer Focus", text: "Built around the practical requirements of farmers and agricultural businesses." },
  { icon: Truck, title: "Reliable Supply", text: "Professional supply solutions for retailers, dealers, and bulk buyers." },
  { icon: TrendingUp, title: "Growing Together", text: "Building lasting relationships across the agricultural ecosystem." },
];

const checkpoints = [
  { icon: ClipboardCheck, title: "Product Selection", text: "Varieties positioned for the practical needs of Bihar's farming conditions." },
  { icon: BadgeCheck, title: "Quality Handling", text: "Careful handling practices across sourcing, storage and dispatch." },
  { icon: PackageCheck, title: "Professional Packaging", text: "Branded, labelled bags that protect the seed and inform the buyer." },
  { icon: Network, title: "Agricultural Distribution", text: "A growing network of retailers, dealers and distributors across Bihar." },
];

export function WhySection() {
  return (
    <section data-testid="why-icropsia-section" className="py-20 sm:py-28 bg-brand-beige/60 relative overflow-hidden">
      <div className="container-site">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-start">
          <Reveal>
            <Eyebrow>Why ICROPSIA</Eyebrow>
            <h2 className="mt-4 font-display text-3xl sm:text-4xl lg:text-[2.75rem] font-extrabold tracking-tight text-brand-deep leading-[1.1]">
              More Than Seeds. <span className="font-editorial italic font-semibold text-brand-gold">A Commitment</span> to Better Farming.
            </h2>
            <p className="mt-5 text-base sm:text-lg text-brand-charcoal/65 leading-relaxed">
              At ICROPSIA, we believe quality agricultural inputs can play an important role in helping farmers pursue
              stronger and more consistent farming outcomes. Our focus is simple: provide quality seed products,
              professional packaging, dependable service, and long-term support to the farming and agricultural
              communities we serve.
            </p>
          </Reveal>
          <div className="grid sm:grid-cols-2 gap-5">
            {features.map(({ icon: Icon, title, text }, i) => (
              <Reveal key={title} delay={i * 0.1}>
                <div className="h-full bg-white rounded-2xl border border-brand-deep/10 p-6 hover:border-brand-gold/50 hover:shadow-lg hover:shadow-brand-deep/5 transition-all duration-300 group">
                  <span className="w-10 h-10 rounded-xl bg-brand-deep flex items-center justify-center mb-4 group-hover:bg-brand-gold transition-colors">
                    <Icon className="w-5 h-5 text-brand-wheat group-hover:text-brand-deep transition-colors" />
                  </span>
                  <h3 className="font-display font-bold text-brand-deep">{title}</h3>
                  <p className="mt-1.5 text-sm text-brand-charcoal/60 leading-relaxed">{text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

export function QualitySection() {
  return (
    <section data-testid="seed-quality-section" className="py-20 sm:py-28">
      <div className="container-site grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
        <Reveal className="relative order-2 lg:order-1">
          <div className="relative rounded-[2rem] overflow-hidden aspect-[4/5] max-w-md mx-auto lg:mx-0 shadow-2xl shadow-brand-deep/20">
            <img
              src="https://images.unsplash.com/photo-1623211269755-569fec0536d2?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200"
              alt="Young green crop growth in cultivated soil"
              loading="lazy"
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-brand-ink/60 to-transparent" />
            <div className="absolute bottom-5 left-5 right-5 bg-white/90 backdrop-blur rounded-2xl p-4 flex items-center gap-3.5">
              <span className="w-10 h-10 rounded-xl bg-brand-gold/20 flex items-center justify-center shrink-0">
                <Wheat className="w-5 h-5 text-brand-deep" />
              </span>
              <p className="text-xs leading-relaxed text-brand-charcoal/75">
                <span className="font-bold text-brand-deep">Batch-level quality fields</span> — germination %, purity %,
                lot number and test date — are published per lot once verified by the company.
              </p>
            </div>
          </div>
          <div className="hidden lg:block absolute -top-6 -left-6 w-24 h-24 rounded-full border-2 border-dashed border-brand-gold/50 animate-spin-slow" />
        </Reveal>

        <div className="order-1 lg:order-2">
          <Reveal>
            <Eyebrow>Seed Quality</Eyebrow>
            <h2 className="mt-4 font-display text-3xl sm:text-4xl font-extrabold tracking-tight text-brand-deep leading-[1.1]">
              Quality You Can See. <span className="font-editorial italic font-semibold text-brand-gold">Confidence</span> You Can Build.
            </h2>
            <p className="mt-5 text-base sm:text-lg text-brand-charcoal/65 leading-relaxed">
              Every farming season begins with a decision about what goes into the soil. ICROPSIA is committed to
              providing carefully positioned seed varieties with professional packaging and clear product information,
              helping farmers and agricultural partners make informed purchasing decisions.
            </p>
          </Reveal>
          <div className="mt-9 space-y-2">
            {checkpoints.map(({ icon: Icon, title, text }, i) => (
              <Reveal key={title} delay={i * 0.08}>
                <div className="flex gap-4 items-start p-4 rounded-2xl hover:bg-brand-beige/60 transition-colors group">
                  <span className="font-display text-sm font-extrabold text-brand-gold/70 pt-1 w-7 shrink-0">0{i + 1}</span>
                  <span className="w-10 h-10 rounded-xl bg-brand-deep/5 border border-brand-deep/10 flex items-center justify-center shrink-0 group-hover:bg-brand-deep transition-colors">
                    <Icon className="w-5 h-5 text-brand-deep group-hover:text-brand-wheat transition-colors" />
                  </span>
                  <div>
                    <h3 className="font-display font-bold text-brand-deep text-sm">{title}</h3>
                    <p className="text-sm text-brand-charcoal/60 leading-relaxed mt-0.5">{text}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
